import { SPECIES } from '@/lib/speciesBreeds';
/**
 * Species presets for the report template editor.
 *
 * Lives beside the dialog rather than inside it so the constants can be shared with tests
 * without tripping react-refresh (a component file may only export components), which is the
 * same reason `requestFields.ts` and `useTemplateScopes.ts` sit here.
 */
/**
 * Presets come from the curated species master rather than a list typed out here, so this
 * editor offers what the pickers that actually write `patient_species` can produce. These two
 * are the explicit departures, and keeping them in a named list is what stops a later
 * "just use SPECIES" from quietly undoing either:
 *
 *   Equine  deliberately retired as a preset. Existing templates that already carry it render
 *           it as a custom chip, so no data is lost. A product decision.
 *   Other   the picker's escape hatch, meaning "a species our list does not carry", not a
 *           species of its own. A template matching 'other' would match nothing in particular,
 *           and the chip would sit directly above the dialog's own free-text lane for exactly
 *           that case.
 */
export const SPECIES_PRESETS_EXCLUDED = ['Equine', 'Other'];
/**
 * Presets are stored LOWERCASE. `reportTemplateMatching` folds both sides before comparing, so
 * title case would still match, but the free-text lane lowercases whatever a user types and the
 * column would then carry two spellings of the same species. Lowercasing on write keeps it to one.
 */
export const SPECIES_OPTIONS = [
    { value: '*', labelKey: 'reporting.templateEditor.allSpecies' },
    ...SPECIES.filter((s) => !SPECIES_PRESETS_EXCLUDED.includes(s)).map((s) => ({
        value: s.toLowerCase(),
        labelKey: `reporting.templateEditor.${s.toLowerCase()}`,
    })),
];
const SPECIES_PRESET_VALUES = SPECIES_OPTIONS.map((o) => o.value);
/** Fold a species the way `reportTemplateMatching` does before comparing. */
export function speciesKey(value) {
    return value.trim().toLowerCase();
}
/**
 * Membership, ignoring case, so one animal is one entry. The system templates seeded by
 * migration store 'Canine' / 'Feline' in title case; compared exactly they would render as
 * custom chips AND leave their own preset looking unselected, i.e. the same species twice, and
 * ticking the preset would save both spellings.
 */
export function hasSpecies(list, value) {
    const key = speciesKey(value);
    return list.some((s) => speciesKey(s) === key);
}
/** True when a preset chip already covers this stored value. */
export function isPresetSpecies(value) {
    return hasSpecies(SPECIES_PRESET_VALUES, value);
}
